import { useState } from 'react';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Pagination,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import ContainerLayout from '@/components/layout/ContainerLayout';
import { listPayments, Pagamento } from '@/services/api/payment/payment.service';

const columnHelper = createColumnHelper<Pagamento>();

const PaymentTablePage = () => {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const { isLoading, data } = useQuery({
    // Chave própria: com 'eventsTable' (a da tabela de eventos) esta página recebia a lista de
    // eventos do cache e quebrava ao ler inscriptionId, deixando a tela branca.
    queryKey: ['paymentsTable'],
    queryFn: async () => {
      const { result } = await listPayments();
      return result;
    },
  });

  const columns = [
    columnHelper.accessor((row) => row.inscriptionId?.athletes?.[0]?.name, {
      id: 'nomeLider', // Necessário quando o accessor é uma função
      header: 'Nome Líder',
      filterFn: 'includesString',
      cell: (info) => (
        <Text tt="uppercase" lineClamp={1}>
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('eventId.name', {
      header: 'Nome Evento',
      filterFn: 'includesString',
      cell: (info) => (
        <Text tt="uppercase" lineClamp={1}>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('status', {
      header: 'Status Pagamento',
      filterFn: 'includesString',
      cell: (info) => (
        <Text tt="uppercase" lineClamp={1}>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('transactionDetails.net_received_amount', {
      header: 'Valor Recebido Org',
      filterFn: 'includesString',
      cell: (info) => {
        const value = info.getValue();
        return (
          <Text fw={500}>
            {value
              ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
              : 'R$ 0,00'}
          </Text>
        );
      },
    }),
    columnHelper.accessor(
      (row) => row.feeDetails?.find((fee) => fee.type === 'application_fee')?.amount,
      {
        id: 'valorRank',
        header: 'Valor Recebido Rank',
        cell: (info) => {
          const value = info.getValue();
          return (
            <Text fw={500}>
              {value
                ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                : 'R$ 0,00'}
            </Text>
          );
        },
      }
    ),
  ];

  const table = useReactTable({
    data: data || [],
    columns,
    state: { globalFilter, pagination },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
  });

  return (
    <ContainerLayout>
      <Group justify="space-between" mb={20}>
        <TextInput
          size="md"
          radius="lg"
          placeholder="Busque pelo evento..."
          leftSection={<IconSearch size={24} stroke={1.5} />}
          value={globalFilter ?? ''}
          onChange={(event) => setGlobalFilter(event.currentTarget.value)}
        />
        <Button
          color="principal.9"
          onClick={() => navigate('/dashboard/evento/cadastrar/novo')}
          leftSection={<IconPlus />}
        >
          CRIAR PAGAMENTO
        </Button>
      </Group>
      <Divider />
      {isLoading ? (
        <Center>
          <Loader size={20} color="principal" type="bars" />
        </Center>
      ) : (
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.Th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.Th>
                ))}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {table.getRowModel().rows.map((row) => (
              <Table.Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Table.Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <Center>
        <Pagination
          total={table.getPageCount()}
          value={table.getState().pagination.pageIndex + 1}
          onChange={(page) => setPagination((prev) => ({ ...prev, pageIndex: page - 1 }))}
          withEdges
          mt={20}
          color="principal.9"
        />
      </Center>
    </ContainerLayout>
  );
};

export default PaymentTablePage;
